/**
 * Loading state display during analysis processing
 */

import { useState, useEffect } from 'react';

import { Card } from '@/components/ui/Card';

const processingSteps = [
  'Extracting video frames...',
  'Analyzing visual content...',
  'Processing audio transcript...',
  'Detecting brand elements...',
  'Evaluating quality metrics...',
  'Generating recommendations...',
];

const funFacts = [
  'Videos with brand cues in the first 2 seconds perform 30% better on Meta platforms.',
  'The average attention span for social media ads is under 3 seconds.',
  'Clear product visibility correlates strongly with conversion rates.',
  'Platform algorithms favor videos with high early engagement.',
  'Most effective ads follow a 3-part structure: Hook, Message, CTA.',
  'Color psychology can increase brand recall by up to 40%.',
];

export interface AnalysisLoadingProps {
  startTime?: Date;
  estimatedSeconds?: number;
}

export function AnalysisLoading({
  startTime = new Date(),
  estimatedSeconds = 60,
}: AnalysisLoadingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [funFact] = useState(() => funFacts[Math.floor(Math.random() * funFacts.length)]);

  useEffect(() => {
    // Update elapsed time every second
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  useEffect(() => {
    // Cycle through processing steps
    const stepDuration = (estimatedSeconds * 1000) / processingSteps.length;
    const stepTimer = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % processingSteps.length);
    }, stepDuration);

    return () => clearInterval(stepTimer);
  }, [estimatedSeconds]);

  const progress = Math.min((elapsedSeconds / estimatedSeconds) * 100, 95);
  const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);

  return (
    <Card className="p-8">
      <div className="flex flex-col items-center justify-center">
        {/* Animated Spinner */}
        <div className="relative mb-6">
          <svg
            className="w-20 h-20 animate-spin"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#E7ECF6"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="#2563FF"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${progress * 2.827} 282.7`}
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-gray-900">
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {/* Status Message */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Analyzing your creative...
        </h3>

        {/* Current Step */}
        <p className="text-sm text-gray-600 mb-6 animate-pulse">
          {processingSteps[currentStep]}
        </p>

        {/* Progress Steps */}
        <div className="w-full max-w-md mb-6">
          <div className="flex items-center justify-between mb-2">
            {processingSteps.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 mx-0.5 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Estimated Time */}
        <div className="text-sm text-gray-500 mb-8">
          {remainingSeconds > 0 ? (
            <>
              Estimated time remaining:{' '}
              <span className="font-semibold text-gray-700">
                {remainingSeconds}s
              </span>
            </>
          ) : (
            <span className="text-orange-600 font-semibold">
              Finalizing analysis...
            </span>
          )}
        </div>

        {/* Fun Fact */}
        <div className="w-full max-w-md p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">
                Did you know?
              </div>
              <p className="text-sm text-blue-900 leading-relaxed">{funFact}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
